/**
 * ContactOptionsExpanded Component
 * 
 * Displays a minimal popover menu with contact options (WhatsApp and Chat)
 * when the main floating button is clicked in collapsed state.
 * 
 * Design: Uses shadcn Popover with primary/secondary color accents.
 */

import React, { useState, useRef } from 'react';
import { cn } from '@/lib/utils';
import chatBotIconComponent from '/src/assets/chatBotIconComponent.svg';
import whatsappIcon from '/src/assets/WhatsappIcon.svg';

interface ContactOptionsExpandedProps {
  onClose: () => void;
  onChatClick: () => void;
  onWhatsAppClick: (token?: string | null) => void;
  unreadCount?: number;
  isAuthenticated: boolean;
  token: string | null;
  hasPhoneNumber: boolean;
}

export default function ContactOptionsExpanded({
  onClose,
  onChatClick,
  onWhatsAppClick,
  isAuthenticated,
  token,
  hasPhoneNumber,
}: ContactOptionsExpandedProps) {
  const [buzz, setBuzz] = useState(false);
  const buzzTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // WhatsApp helper text logic
  let whatsappHelper: string | null = null;
  if (!isAuthenticated) {
    whatsappHelper = 'Requiere autenticación';
  } else if (!hasPhoneNumber) {
    whatsappHelper = 'Completar perfil';
  }

  // Handler for WhatsApp button
  const handleWhatsAppClick = (e: React.MouseEvent) => {
    if (!isAuthenticated) {
      e.preventDefault();
      setBuzz(true);
      
      if (buzzTimeoutRef.current) {
        clearTimeout(buzzTimeoutRef.current);
      }
      buzzTimeoutRef.current = setTimeout(() => setBuzz(false), 500);
      
      // Dispatch custom event to trigger login from outside Shadow DOM
      const loginEvent = new CustomEvent('chatbot-trigger-login', {
        bubbles: true,
        composed: true,
        detail: {
          source: 'whatsapp-button',
          reason: 'authentication-required',
          token: token
        }
      });
      window.dispatchEvent(loginEvent);
      
      return;
    }

    // Pass token to the WhatsApp click handler if authenticated
    if (token) {
      onWhatsAppClick(token);
    } else {
      onWhatsAppClick();
    }
  };

  return (
    <>
      {/* Invisible overlay to close */}
      <div 
        className="fixed inset-0 z-9997" 
        onClick={onClose}
        aria-label="Cerrar menú"
      />

      {/* Menu card with custom positioning */}
      <div 
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "fixed bottom-24 lg:bottom-28 right-6 z-10000",
          "w-64 p-2 rounded-xl",
          "bg-primary text-primary-foreground",
          "border border-primary-foreground/20",
          "shadow-xl",
          "animate-[scaleIn_0.2s_ease-out_forwards]",
          "origin-bottom-right"
        )}
      >
          {/* Chat option - Primary action */}
          <button
            onClick={onChatClick}
            className={cn(
              "w-full flex items-center gap-3 p-3 rounded-lg",
              "text-left transition-all duration-200",
              "hover:bg-primary-foreground/10",
              "active:scale-[0.98]",
              "outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/50",
              "cursor-pointer",
              "group"
            )}
            aria-label="Abrir chat"
          >
            <div className={cn(
              "shrink-0 w-11 h-11 flex items-center justify-center rounded-full",
              "bg-primary-foreground/15",
              "group-hover:bg-primary-foreground/20",
              "transition-colors duration-200"
            )}>
              <img 
                src={chatBotIconComponent} 
                alt="" 
                className="h-5 w-5 opacity-90"
              />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">
                Chat
              </div>
              <div className="text-xs opacity-80 truncate">
                Conversación en el sitio web
              </div>
            </div>
          </button>

          <div className="h-px bg-primary-foreground/15 my-1.5" />

          {/* WhatsApp option - Secondary action */}
          <button
            onClick={handleWhatsAppClick}
            className={cn(
              "w-full flex items-center gap-3 p-3 rounded-lg",
              "text-left transition-all duration-200",
              "hover:bg-primary-foreground/10",
              "active:scale-[0.98]",
              "outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/50",
              "cursor-pointer",
              buzz && 'animate-[buzz_0.5s_ease-out]',
              "group"
            )}
            aria-label="Contactar por WhatsApp"
          >
            <div className={cn(
              "shrink-0 w-11 h-11 flex items-center justify-center rounded-full",
              "bg-[#25D366]",
              "group-hover:bg-[#20BA5A]",
              "transition-colors duration-200"
            )}>
              <img 
                src={whatsappIcon} 
                alt="" 
                className="h-5 w-5 opacity-90"
              />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">
                WhatsApp
              </div>
              <div className="text-xs opacity-80 truncate">
                {whatsappHelper || 'Chatea desde tu teléfono'}
              </div>
            </div>
          </button>
        </div>
    </>
  );
}
